import {TabView} from "primereact/tabview";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {matchPath, Outlet, useLocation} from "react-router-dom";

import LoadingIndicator from "../component/LoadingIndicator";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import MissingPermissionIndicator from "../component/MissingPermissionIndicator";
import {getTabPanelWithPermission} from "../component/TabPanelWithPermission";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {useCheckPermissionRequirement} from "../hooks/permissionHooks";
import {selectPermissions} from "../redux/authSlice";
import {selectInitialized as selectDocumentInitialized, selectLoading} from "../redux/documentsSlice";
import {loadDocumentsAsync} from "../redux/documentsThunk";
import {selectIsSnapshot, selectPersistenceInitialized, selectPersistenceLoading} from "../redux/persistenceSlice";
import {selectIndicationData} from "../redux/valueIndicationSlice";
import {useEffectRequiresValidRevaId} from "../util/hooks";
import {PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS} from "../util/permissions";
import {RevaPermission} from "../util/revaPermission";
import ValueToolbar from "./value/ValueToolbar";

/**
 * @typedef {ValueIndicationResponse | null} ValueIndicationResponseOpt
 */

/**
 * @typedef {TabHeaderWithPermissionParams} TabHeaderWithPermissionParamsForIndication
 * @property {(ValueIndicationResponseOpt) => boolean} [disabledFn] dynamic disabled flag computation
 */

/** @type {TabHeaderWithPermissionParamsForIndication[]} */
const TABS = [
    {
        route: "value",
        headerI18n: "value.category.convergenceValue",
        disabledFn: (indicationData) => !indicationData,
        appreciationKeyTab: "value",
    },
    {
        route: "comparative-value",
        headerI18n: "value.category.comparativeValue",
        disabledFn: (indicationData) => !indicationData?.comparative_value_details,
        appreciationKeyTab: "comparative_value",
    },
    {
        route: "real-asset-value",
        headerI18n: "value.category.realAssetValue",
        disabledFn: (indicationData) => !indicationData?.real_asset_value_details,
        appreciationKeyTab: "real_asset_value",
    },
    {
        route: "income-value",
        headerI18n: "value.category.incomeValue",
        disabledFn: (indicationData) => !indicationData?.income_value_details,
        appreciationKeyTab: "income_value",
    },
    // Ohne Würdigung (#19096). Ohne `indication-energy-value` fehlt der Energieblock in der Wertantwort
    // stumm (#19381) — dann zeigt der Tab das Schloss statt eines leeren Ergebnisses.
    {
        route: "energy-value",
        headerI18n: "value.category.energyValue",
        permissionRule: RevaPermission.INDICATION_ENERGY_VALUE,
        disabledFn: (indicationData) => !indicationData?.energy_value_details,
    },
];
const TAB_ROUTES = TABS.map((tab) => tab.route);

/**
 * @returns {React.JSX.Element}
 */
function PageValue() {
    const {t} = useTranslation();

    const navigate = useNavigateWithQuery();
    const location = useLocation();
    const dispatch = useDispatch();
    const {pathname} = location;

    const valueIndicationData = useSelector(selectIndicationData);
    const isSnapshot = useSelector(selectIsSnapshot);

    const permissions = useSelector(selectPermissions);

    const [appreciationForTabVisible, setAppreciationForTabVisible] = useState(false);

    const persistenceInitialized = useSelector(selectPersistenceInitialized);
    const loading = useSelector(selectLoading);
    // True while a whole valuation/snapshot is being (re)loaded, e.g. when switching the loaded
    // data state via the lifecycle menu. The cleared store would otherwise make the tabs flash
    // the "missing data" indicator during the load — show a loading indicator instead.
    const persistenceLoading = useSelector(selectPersistenceLoading);
    const documentsInitialized = useSelector(selectDocumentInitialized);

    /** @type React.ReactElement[] */
    const tabs = TABS.map((tab) => {
        const {route, headerI18n, disabledFn, permissionRule} = tab;
        let {disabled} = tab;
        if (typeof disabledFn === "function") {
            disabled = disabledFn(valueIndicationData);
        }
        // Ein Snapshot zeigt seine eingefrorenen Daten unabhängig davon, was der Betrachter heute darf —
        // wie die Seite selbst, die ihre Berechtigungen nur außerhalb von Snapshots prüft (#19381).
        return {route, headerI18n, disabled, permissionRule: isSnapshot ? null : permissionRule};
    }).map((tab) => getTabPanelWithPermission(tab, t, permissions));

    useEffectRequiresValidRevaId();

    useEffect(() => {
        if (persistenceInitialized && !documentsInitialized && !loading) {
            // Initially load documents after persistence was initialized, if documents have not been initialized/loading yet for pdf generation
            dispatch(loadDocumentsAsync());
        }
    }, [persistenceInitialized, documentsInitialized, loading, dispatch]);

    let activeIndex = null;
    for (let index = 0; index < TAB_ROUTES.length; index++) {
        if (matchPath(`/value/${TAB_ROUTES[index]}`, pathname)) {
            activeIndex = index;
            break;
        }
    }
    const appreciationKeyTab = TABS[activeIndex].appreciationKeyTab;

    const onTabChange = (index) => {
        if (index >= 0 && index < TAB_ROUTES.length) {
            setAppreciationForTabVisible(false);
            navigate(`/value/${TAB_ROUTES[index]}`);
        }
    };

    const hasAnyPermissions = useCheckPermissionRequirement(PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS);
    if (!hasAnyPermissions) {
        // While the valuation is still (re)loading its snapshot state is not yet known, so the
        // permission requirement cannot be resolved reliably. Show a loading indicator instead of
        // briefly flashing the missing-permission hint until we can tell whether this is a
        // snapshot (which needs no indication permission). See #18799.
        if (persistenceLoading || !persistenceInitialized) {
            return <LoadingIndicator loading />;
        }
        return <MissingPermissionIndicator missingPermissions={true} />;
    }

    return (
        <MainFrame>
            <MainToolbar title={t("value.valueIndication")}>
                <TabView
                    className="flex-1 HeaderTabView"
                    panelContainerClassName="hidden"
                    activeIndex={activeIndex}
                    onTabChange={(e) => onTabChange(e.index)}
                >
                    {tabs}
                </TabView>
                <ValueToolbar
                    appreciationKeyTab={appreciationKeyTab}
                    appreciationForTabVisible={appreciationForTabVisible}
                    setAppreciationForTabVisible={setAppreciationForTabVisible}
                />
            </MainToolbar>
            {persistenceLoading ? (
                <LoadingIndicator loading />
            ) : (
                <Outlet
                    context={{
                        appreciation: {
                            page: "value",
                            tab: appreciationKeyTab,
                            visible: appreciationForTabVisible,
                        },
                    }}
                />
            )}
        </MainFrame>
    );
}

export default PageValue;
